import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { payConfigGateway } from '../../pay-config';
import type { DeductionCodeResponse } from '../../pay-config/types';
import { compensationGateway } from '../index';
import type { BenefitCodeResponse, BenefitElectionResponse, DeductionElectionResponse } from '../types';
import '../compensation.css';

// The canonical employee-election surface for EPN-38. BENEFIT elections use
// BenefitCode -> EmployeeBenefitElection; DEDUCTION elections use DeductionCode -> EmployeeDeductionElection.
// These are two different backend concepts sharing one panel deliberately — never merged into one fake
// combined entity. Company-level BenefitCode/DeductionCode definitions are only ever listed here, never
// created here (see the module-boundary note on CompensationGateway).
//
// This is an embeddable, employee-scoped PANEL, not a standalone page or route — it belongs inside
// Employee Profile Setup -> Additional Settings -> Benefits / Deductions. It takes the employee it edits
// from its parent (the `employeeId` prop) rather than selecting one itself: no employee search, no
// employee dropdown, no listAllEmployees call. The employee is already implied by whatever screen renders
// this panel (the eventual Employee Profile shell). Company context remains controlled by the existing
// application/backend context (ICurrentUserContext server-side) — this panel never adds a second
// active-company selector.

export interface BenefitsDeductionsPanelProps {
  employeeId: string;
  /** Shows only the benefit-plan elections (no Benefits / Deductions tab bar). Used under the Step 4 table, which already covers deductions. */
  benefitsOnly?: boolean;
}

type Tab = 'benefits' | 'deductions';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function parseOptionalNumber(raw: string): number | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : Number(trimmed);
}

export function BenefitsDeductionsPanel({ employeeId, benefitsOnly = false }: BenefitsDeductionsPanelProps) {
  const [tab, setTab] = useState<Tab>('benefits');

  const [benefitCodes, setBenefitCodes] = useState<BenefitCodeResponse[]>([]);
  const [deductionCodes, setDeductionCodes] = useState<DeductionCodeResponse[]>([]);
  const [benefitElections, setBenefitElections] = useState<BenefitElectionResponse[]>([]);
  const [deductionElections, setDeductionElections] = useState<DeductionElectionResponse[]>([]);

  const [loadingElections, setLoadingElections] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAddBenefit, setShowAddBenefit] = useState(false);
  const [abCodeId, setAbCodeId] = useState('');
  const [abEffectiveStartDate, setAbEffectiveStartDate] = useState(todayIso());
  const [abEmployeeContributionOverride, setAbEmployeeContributionOverride] = useState('');
  const [abCompanyContributionOverride, setAbCompanyContributionOverride] = useState('');
  const [abAmountOverride, setAbAmountOverride] = useState('');
  const [abError, setAbError] = useState<string | null>(null);

  const [showAddDeduction, setShowAddDeduction] = useState(false);
  const [adCodeId, setAdCodeId] = useState('');
  const [adEffectiveStartDate, setAdEffectiveStartDate] = useState(todayIso());
  const [adAmountOverride, setAdAmountOverride] = useState('');
  const [adCompanyContributionOverride, setAdCompanyContributionOverride] = useState('');
  const [adError, setAdError] = useState<string | null>(null);

  const [endingId, setEndingId] = useState<string | null>(null);
  const [endDate, setEndDate] = useState(todayIso());
  const [endError, setEndError] = useState<string | null>(null);

  const loadCodes = useCallback(async () => {
    try {
      const [benefits, deductions] = await Promise.all([
        compensationGateway.listBenefitCodes(),
        payConfigGateway.listDeductionCodes(),
      ]);
      setBenefitCodes(benefits);
      setDeductionCodes(deductions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load benefit/deduction definitions.');
    }
  }, []);

  const loadElections = useCallback(async (id: string) => {
    setLoadingElections(true);
    setError(null);
    try {
      const [benefits, deductions] = await Promise.all([
        compensationGateway.listBenefitElections(id),
        compensationGateway.listDeductionElections(id),
      ]);
      setBenefitElections(benefits);
      setDeductionElections(deductions);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this employee's benefits/deductions.");
    } finally {
      setLoadingElections(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadCodes();
  }, [loadCodes]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadElections(employeeId);
  }, [employeeId, loadElections]);

  function benefitCodeName(id: string): string {
    return benefitCodes.find((c) => c.id === id)?.name ?? 'Unknown benefit code';
  }

  function deductionCodeName(id: string): string {
    return deductionCodes.find((c) => c.id === id)?.name ?? 'Unknown deduction code';
  }

  const eligibleBenefitCodes = benefitCodes.filter((c) => c.isActive);
  const eligibleDeductionCodes = deductionCodes.filter((c) => c.isActive);

  function openAddBenefit() {
    setAbCodeId('');
    setAbEffectiveStartDate(todayIso());
    setAbEmployeeContributionOverride('');
    setAbCompanyContributionOverride('');
    setAbAmountOverride('');
    setAbError(null);
    setShowAddBenefit(true);
  }

  function openAddDeduction() {
    setAdCodeId('');
    setAdEffectiveStartDate(todayIso());
    setAdAmountOverride('');
    setAdCompanyContributionOverride('');
    setAdError(null);
    setShowAddDeduction(true);
  }

  async function handleSaveBenefit(e: FormEvent) {
    e.preventDefault();
    setAbError(null);
    if (!abCodeId) {
      setAbError('Select a benefit to add.');
      return;
    }
    if (!abEffectiveStartDate) {
      setAbError('Effective date is required.');
      return;
    }
    try {
      await compensationGateway.createBenefitElection(employeeId, {
        benefitCodeId: abCodeId,
        employeeContributionPercentOverride: parseOptionalNumber(abEmployeeContributionOverride),
        companyContributionPercentOverride: parseOptionalNumber(abCompanyContributionOverride),
        amountOverride: parseOptionalNumber(abAmountOverride),
        effectiveStartDate: abEffectiveStartDate,
      });
      setShowAddBenefit(false);
      await loadElections(employeeId);
    } catch (err) {
      setAbError(err instanceof Error ? err.message : 'Could not save this benefit election.');
    }
  }

  async function handleSaveDeduction(e: FormEvent) {
    e.preventDefault();
    setAdError(null);
    if (!adCodeId) {
      setAdError('Select a deduction to add.');
      return;
    }
    if (!adEffectiveStartDate) {
      setAdError('Effective date is required.');
      return;
    }
    try {
      await compensationGateway.createDeductionElection(employeeId, {
        deductionCodeId: adCodeId,
        amountOverride: parseOptionalNumber(adAmountOverride),
        companyContributionOverride: parseOptionalNumber(adCompanyContributionOverride),
        effectiveStartDate: adEffectiveStartDate,
      });
      setShowAddDeduction(false);
      await loadElections(employeeId);
    } catch (err) {
      setAdError(err instanceof Error ? err.message : 'Could not save this deduction election.');
    }
  }

  function startEnding(id: string) {
    setEndingId(id);
    setEndDate(todayIso());
    setEndError(null);
  }

  async function confirmEnd(kind: Tab) {
    if (!endingId) return;
    setEndError(null);
    try {
      if (kind === 'benefits') {
        await compensationGateway.endBenefitElection(endingId, endDate);
      } else {
        await compensationGateway.endDeductionElection(endingId, endDate);
      }
      setEndingId(null);
      await loadElections(employeeId);
    } catch (err) {
      setEndError(err instanceof Error ? err.message : 'Could not end this election.');
    }
  }

  const activeBenefitCount = benefitElections.filter((e) => e.effectiveEndDate === null).length;
  const activeDeductionCount = deductionElections.filter((e) => e.effectiveEndDate === null).length;

  return (
    <div className="compensation-panel">
      {error && <p className="error">{error}</p>}

      {!benefitsOnly && <div className="tabs">
        <button type="button" className={tab === 'benefits' ? 'tab tab--active' : 'tab'} onClick={() => setTab('benefits')}>
          Benefits ({activeBenefitCount} active)
        </button>
        <button type="button" className={tab === 'deductions' ? 'tab tab--active' : 'tab'} onClick={() => setTab('deductions')}>
          Deductions ({activeDeductionCount} active)
        </button>
      </div>}

      {loadingElections && <p className="muted">Loading…</p>}

      {!loadingElections && tab === 'benefits' && (
        <>
          <div className="section-header">
            <h3>Benefit elections</h3>
            <button type="button" onClick={openAddBenefit}>Add Benefit</button>
          </div>

          {benefitElections.length === 0 && <p className="muted">No benefit elections for this employee yet.</p>}
          {benefitElections.length > 0 && (
            <table>
              <thead>
                <tr><th>Benefit</th><th>Employee %</th><th>Company %</th><th>Amount</th><th>Effective</th><th>Ends</th><th></th></tr>
              </thead>
              <tbody>
                {benefitElections.map((election) => (
                  <tr key={election.id}>
                    <td>{benefitCodeName(election.benefitCodeId)}</td>
                    <td>{election.employeeContributionPercentOverride ?? '—'}</td>
                    <td>{election.companyContributionPercentOverride ?? '—'}</td>
                    <td>{election.amountOverride ?? '—'}</td>
                    <td>{election.effectiveStartDate}</td>
                    <td>{election.effectiveEndDate ?? 'Open'}</td>
                    <td>
                      {election.effectiveEndDate === null && endingId !== election.id && (
                        <button type="button" className="link" onClick={() => startEnding(election.id)}>End</button>
                      )}
                      {endingId === election.id && (
                        <span className="compensation-end-confirm">
                          <label>
                            End date
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                          </label>
                          <button type="button" onClick={() => void confirmEnd('benefits')}>Confirm end</button>
                          <button type="button" className="link" onClick={() => setEndingId(null)}>Cancel</button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {endingId && endError && tab === 'benefits' && <p className="error">{endError}</p>}
        </>
      )}

      {!loadingElections && tab === 'deductions' && (
        <>
          <div className="section-header">
            <h3>Deduction elections</h3>
            <button type="button" onClick={openAddDeduction}>Add Deduction</button>
          </div>

          {deductionElections.length === 0 && <p className="muted">No deduction elections for this employee yet.</p>}
          {deductionElections.length > 0 && (
            <table>
              <thead>
                <tr><th>Deduction</th><th>Amount</th><th>Company contribution</th><th>Effective</th><th>Ends</th><th></th></tr>
              </thead>
              <tbody>
                {deductionElections.map((election) => (
                  <tr key={election.id}>
                    <td>{deductionCodeName(election.deductionCodeId)}</td>
                    <td>{election.amountOverride ?? '—'}</td>
                    <td>{election.companyContributionOverride ?? '—'}</td>
                    <td>{election.effectiveStartDate}</td>
                    <td>{election.effectiveEndDate ?? 'Open'}</td>
                    <td>
                      {election.effectiveEndDate === null && endingId !== election.id && (
                        <button type="button" className="link" onClick={() => startEnding(election.id)}>End</button>
                      )}
                      {endingId === election.id && (
                        <span className="compensation-end-confirm">
                          <label>
                            End date
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                          </label>
                          <button type="button" onClick={() => void confirmEnd('deductions')}>Confirm end</button>
                          <button type="button" className="link" onClick={() => setEndingId(null)}>Cancel</button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {endingId && endError && tab === 'deductions' && <p className="error">{endError}</p>}
        </>
      )}

      {showAddBenefit && (
        <div className="compensation-modal-backdrop" role="presentation" onClick={() => setShowAddBenefit(false)}>
          <div className="compensation-modal" role="dialog" aria-modal="true" aria-label="Add Benefit" onClick={(e) => e.stopPropagation()}>
            <div className="compensation-modal-head">
              <h3>Add Benefit</h3>
              <button type="button" className="compensation-modal-close" aria-label="Close" onClick={() => setShowAddBenefit(false)}>×</button>
            </div>
            <form onSubmit={handleSaveBenefit}>
              <div className="compensation-modal-body">
                {eligibleBenefitCodes.length === 0 ? (
                  <p className="muted">No active benefit plans are set up for this company yet.</p>
                ) : (
                  <>
                    <label>
                      Benefit
                      <select aria-label="Benefit" value={abCodeId} onChange={(e) => setAbCodeId(e.target.value)} required>
                        <option value="">Select a benefit…</option>
                        {eligibleBenefitCodes.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Effective date
                      <input type="date" value={abEffectiveStartDate} onChange={(e) => setAbEffectiveStartDate(e.target.value)} required />
                    </label>
                    <label>
                      Employee contribution % override (optional)
                      <input type="number" step="0.01" value={abEmployeeContributionOverride} onChange={(e) => setAbEmployeeContributionOverride(e.target.value)} />
                    </label>
                    <label>
                      Company contribution % override (optional)
                      <input type="number" step="0.01" value={abCompanyContributionOverride} onChange={(e) => setAbCompanyContributionOverride(e.target.value)} />
                    </label>
                    <label>
                      Amount override (optional)
                      <input type="number" step="0.01" value={abAmountOverride} onChange={(e) => setAbAmountOverride(e.target.value)} />
                    </label>
                  </>
                )}
                {abError && <p className="error">{abError}</p>}
              </div>
              <div className="compensation-modal-footer">
                <button type="button" className="link" onClick={() => setShowAddBenefit(false)}>Cancel</button>
                <button type="submit" disabled={eligibleBenefitCodes.length === 0 || !abCodeId || !abEffectiveStartDate}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddDeduction && (
        <div className="compensation-modal-backdrop" role="presentation" onClick={() => setShowAddDeduction(false)}>
          <div className="compensation-modal" role="dialog" aria-modal="true" aria-label="Add Deduction" onClick={(e) => e.stopPropagation()}>
            <div className="compensation-modal-head">
              <h3>Add Deduction</h3>
              <button type="button" className="compensation-modal-close" aria-label="Close" onClick={() => setShowAddDeduction(false)}>×</button>
            </div>
            <form onSubmit={handleSaveDeduction}>
              <div className="compensation-modal-body">
                {eligibleDeductionCodes.length === 0 ? (
                  <p className="muted">No active deduction codes are set up for this company yet.</p>
                ) : (
                  <>
                    <label>
                      Deduction
                      <select aria-label="Deduction" value={adCodeId} onChange={(e) => setAdCodeId(e.target.value)} required>
                        <option value="">Select a deduction…</option>
                        {eligibleDeductionCodes.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Effective date
                      <input type="date" value={adEffectiveStartDate} onChange={(e) => setAdEffectiveStartDate(e.target.value)} required />
                    </label>
                    <label>
                      Amount override (optional)
                      <input type="number" step="0.01" value={adAmountOverride} onChange={(e) => setAdAmountOverride(e.target.value)} />
                    </label>
                    <label>
                      Company contribution override (optional)
                      <input type="number" step="0.01" value={adCompanyContributionOverride} onChange={(e) => setAdCompanyContributionOverride(e.target.value)} />
                    </label>
                  </>
                )}
                {adError && <p className="error">{adError}</p>}
              </div>
              <div className="compensation-modal-footer">
                <button type="button" className="link" onClick={() => setShowAddDeduction(false)}>Cancel</button>
                <button type="submit" disabled={eligibleDeductionCodes.length === 0 || !adCodeId || !adEffectiveStartDate}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
